import React, { useState } from "react";
import { MdLock, MdPublic, MdSend } from "react-icons/md";
import { toast } from "react-toastify";
import AnexosInput from "../../../../components/anexos/AnexosInput";
import AnexosLista from "../../../../components/anexos/AnexosLista";
import { mensagemDeErro } from "../../../../components/anexos/anexosRegras";
import { TicketAttachment, TicketComment } from "../../../../interfaces/Ticket";
import { downloadTicketAttachment, sendTicketMessage } from "../../../../services/ticketApi";

interface Props {
  ticketId: number;
  comments: TicketComment[];
  canComment: boolean;
  onAdded: (comment: TicketComment) => void;
}

const baixarAnexo = async (a: TicketAttachment) => {
  try {
    await downloadTicketAttachment(a.id, a.fileName);
  } catch {
    toast.error("Não foi possível baixar o anexo.");
  }
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Thread de comentários do chamado.
 *
 * ⚠ Decisão de UX central desta tela: público vs. nota interna precisa ser inconfundível ANTES de
 * enviar, não só depois. Escrever uma nota interna achando que é pública (ou o contrário) é o erro
 * caro deste tipo de tela — o cliente lê algo que era para ser interno, ou a equipe nunca vê uma
 * nota que achou pública.
 *
 * Por isso: (1) cada bolha da thread tem cor, ícone e rótulo diferentes — nunca só um selo pequeno;
 * (2) o COMPOSITOR muda de cor por completo (borda, fundo, faixa de aviso e texto do botão) quando
 * a pessoa alterna o modo, para o estado do formulário nunca ficar ambíguo enquanto se digita.
 */
const TicketComments: React.FC<Props> = ({ ticketId, comments, canComment, onAdded }) => {
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const podeEnviar = body.trim().length > 0 || arquivos.length > 0;

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!podeEnviar) return;
    setSending(true);
    try {
      const created = await sendTicketMessage(ticketId, { body: trimmed, isInternal, arquivos });
      onAdded(created);
      setBody("");
      setArquivos([]);
      toast.success(isInternal ? "Nota interna adicionada." : "Resposta enviada ao cliente.");
    } catch (e: any) {
      toast.error(mensagemDeErro(e, "Não foi possível enviar o comentário."));
    } finally {
      setSending(false);
    }
  };

  const ordered = [...comments].sort(
    (a, b) => new Date(a.dtCreated).getTime() - new Date(b.dtCreated).getTime()
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        {ordered.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            Nenhum comentário ainda.
          </p>
        ) : (
          ordered.map((c) =>
            c.isInternal ? (
              <div
                key={c.id}
                className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3.5 dark:border-amber-500/40 dark:bg-amber-900/20"
              >
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    <MdLock className="h-3.5 w-3.5" aria-hidden="true" />
                    Nota interna — o cliente não vê
                  </span>
                  <span className="text-[11px] text-amber-700/70 dark:text-amber-400/70">
                    {fmtDateTime(c.dtCreated)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-amber-900 dark:text-amber-100">
                  {c.body}
                </p>
                {c.attachments && c.attachments.length > 0 && (
                  <div className="mt-2">
                    <AnexosLista anexos={c.attachments} onBaixar={baixarAnexo} />
                  </div>
                )}
                <p className="mt-1.5 text-xs font-medium text-amber-700/80 dark:text-amber-300/80">
                  {c.authorName ?? "Equipe"}
                </p>
              </div>
            ) : (
              <div
                key={c.id}
                className="rounded-xl border border-gray-200 bg-white p-3.5 dark:border-white/10 dark:bg-navy-900"
              >
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-dv-blue-600 dark:text-dv-cyan-300">
                    <MdPublic className="h-3.5 w-3.5" aria-hidden="true" />
                    Resposta pública — o cliente vê
                  </span>
                  <span className="text-[11px] text-gray-400">{fmtDateTime(c.dtCreated)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-navy-700 dark:text-gray-100">
                  {c.body}
                </p>
                {c.attachments && c.attachments.length > 0 && (
                  <div className="mt-2">
                    <AnexosLista anexos={c.attachments} onBaixar={baixarAnexo} />
                  </div>
                )}
                <p className="mt-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {c.authorName ?? "Equipe"}
                </p>
              </div>
            )
          )
        )}
      </div>

      {canComment && (
        <div
          className={`rounded-2xl border-2 p-3.5 transition-colors ${
            isInternal
              ? "border-amber-300 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-900/10"
              : "border-dv-blue-200 bg-blue-50/40 dark:border-dv-cyan-500/30 dark:bg-navy-900"
          }`}
        >
          <div className="mb-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsInternal(false)}
              aria-pressed={!isInternal}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                !isInternal
                  ? "bg-dv-blue-500 text-white shadow-dv-sm"
                  : "bg-white text-gray-600 dark:bg-navy-800 dark:text-gray-300"
              }`}
            >
              <MdPublic className="h-4 w-4" aria-hidden="true" />
              Resposta pública
            </button>
            <button
              type="button"
              onClick={() => setIsInternal(true)}
              aria-pressed={isInternal}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-bold transition-all ${
                isInternal
                  ? "bg-amber-500 text-white shadow-dv-sm"
                  : "bg-white text-gray-600 dark:bg-navy-800 dark:text-gray-300"
              }`}
            >
              <MdLock className="h-4 w-4" aria-hidden="true" />
              Nota interna
            </button>
          </div>

          <p
            className={`mb-2 text-xs font-semibold ${
              isInternal
                ? "text-amber-700 dark:text-amber-300"
                : "text-dv-blue-700 dark:text-dv-cyan-300"
            }`}
          >
            {isInternal
              ? "Só a equipe interna vê esta nota. O cliente NUNCA lê isto."
              : "O cliente vai ver esta mensagem no Portal dele."}
          </p>

          <AnexosInput value={arquivos} onChange={setArquivos} disabled={sending}>
          <textarea
            aria-label={isInternal ? "Texto da nota interna" : "Texto da resposta ao cliente"}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder={
              isInternal ? "Ex.: aguardando peça do fornecedor…" : "Escreva a resposta para o cliente…"
            }
            className={`w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition-all dark:bg-navy-900 dark:text-white ${
              isInternal
                ? "border-amber-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 dark:border-amber-500/40"
                : "border-dv-blue-200 focus:border-dv-blue-500 focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10"
            }`}
          />
          </AnexosInput>

          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !podeEnviar}
              className={`flex items-center justify-center gap-1.5 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 ${
                isInternal ? "bg-amber-600 hover:bg-amber-700" : "bg-dv-blue-600 hover:bg-dv-blue-700"
              }`}
            >
              <MdSend className="h-4 w-4" aria-hidden="true" />
              {sending ? "Enviando…" : isInternal ? "Adicionar nota interna" : "Enviar ao cliente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketComments;
